var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { KycLevel, Utils, Validations, useSupport } from '@dfx.swiss/react';
import { Form, StyledButton, StyledButtonWidth, StyledDropdown, StyledFileUpload, StyledInput, StyledVerticalStack, } from '@dfx.swiss/react-components';
import { SupportIssueReason } from '@dfx.swiss/react/dist/definitions/support';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useParams } from 'react-router-dom';
import { ErrorHint } from '../components/error-hint';
import { Layout } from '../components/layout';
import { ReasonLabels } from '../config/labels';
import { useSettingsContext } from '../contexts/settings.context';
import { useKycLevelGuard, useUserGuard } from '../hooks/guard.hook';
import { useNavigation } from '../hooks/navigation.hook';
import { toBase64 } from '../util/utils';
var IssueType;
(function (IssueType) {
    IssueType["GENERAL"] = "General";
    IssueType["TRANSACTION"] = "Transaction";
})(IssueType || (IssueType = {}));
const IssueReasons = {
    [IssueType.GENERAL]: [SupportIssueReason.OTHER],
    [IssueType.TRANSACTION]: [SupportIssueReason.OTHER, SupportIssueReason.FUNDS_NOT_RECEIVED],
};
export function SupportIssueScreen() {
    useUserGuard('/login');
    useKycLevelGuard(KycLevel.Link, '/contact');
    const { id } = useParams();
    const { pathname } = useLocation();
    const { navigate } = useNavigation();
    const rootRef = useRef(null);
    const { createGeneralIssue, createTransactionIssue } = useSupport();
    const { translate, translateError } = useSettingsContext();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState();
    const [issueCreated, setIssueCreated] = useState(false);
    const type = pathname.includes('tx') ? IssueType.TRANSACTION : IssueType.GENERAL;
    const reasons = IssueReasons[type];
    const { control, handleSubmit, formState: { errors, isValid }, setValue, } = useForm({ mode: 'onTouched' });
    useEffect(() => {
        id && setValue('transaction', id);
    }, [id]);
    useEffect(() => {
        reasons.length === 1 && setValue('reason', reasons[0]);
    }, [reasons]);
    function onSubmit(data) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            setIsLoading(true);
            try {
                const request = {
                    name: data.name,
                    reason: data.reason,
                    message: data.message,
                    file: data.file && (yield toBase64(data.file)),
                    fileName: (_a = data.file) === null || _a === void 0 ? void 0 : _a.name,
                };
                switch (type) {
                    case IssueType.GENERAL:
                        yield createGeneralIssue(request);
                        break;
                    case IssueType.TRANSACTION:
                        if (!id)
                            throw new Error('Missing transaction ID');
                        yield createTransactionIssue(+id, request);
                        break;
                }
                setIssueCreated(true);
            }
            catch (e) {
                setError((_b = e.message) !== null && _b !== void 0 ? _b : 'Unknown error');
            }
            finally {
                setIsLoading(false);
            }
        });
    }
    function onDone() {
        switch (type) {
            case IssueType.GENERAL:
                navigate('/');
                break;
            case IssueType.TRANSACTION:
                navigate('/tx');
                break;
        }
    }
    const rules = Utils.createRules({
        name: Validations.Required,
        reason: Validations.Required,
        message: Validations.Required,
    });
    return (_jsx(Layout, Object.assign({ title: translate('screens/support', 'Support issue'), rootRef: rootRef }, { children: issueCreated ? (_jsxs(StyledVerticalStack, Object.assign({ gap: 6, full: true }, { children: [_jsx("p", Object.assign({ className: "text-dfxGray-700" }, { children: translate('screens/support', 'The issue has been successfully submitted. You will be contacted by email.') })), _jsx(StyledButton, { label: translate('general/actions', 'Ok'), onClick: onDone, width: StyledButtonWidth.FULL, isLoading: isLoading })] }))) : (_jsx(Form, Object.assign({ control: control, rules: rules, errors: errors, onSubmit: handleSubmit(onSubmit), translate: translateError }, { children: _jsxs(StyledVerticalStack, Object.assign({ gap: 6, full: true, center: true }, { children: [_jsx(StyledInput, { name: "name", autocomplete: "name", label: translate('screens/support', 'Name'), placeholder: `${translate('screens/kyc', 'John')} ${translate('screens/kyc', 'Doe')}`, full: true }), type === IssueType.TRANSACTION && (_jsx(StyledDropdown, { rootRef: rootRef, label: translate('screens/payment', 'Transaction'), items: [], labelFunc: (item) => `${translate('screens/payment', 'Transaction')} ${item}`, name: "transaction", placeholder: translate('general/actions', 'Select...'), full: true })), reasons.length > 1 && (_jsx(StyledDropdown, { rootRef: rootRef, label: translate('screens/support', 'Reason'), items: reasons, labelFunc: (item) => translate('screens/support', ReasonLabels[item]), name: "reason", placeholder: translate('general/actions', 'Select...'), full: true })), _jsx(StyledInput, { name: "message", label: translate('screens/support', 'Description'), multiLine: true, full: true }), _jsx(StyledFileUpload, { name: "file", label: translate('screens/support', 'File'), placeholder: translate('general/actions', 'Drop files here'), buttonLabel: translate('general/actions', 'Browse'), full: true }), error && (_jsx("div", { children: _jsx(ErrorHint, { message: error }) })), _jsx(StyledButton, { type: "submit", label: translate('general/actions', 'Next'), onClick: handleSubmit(onSubmit), width: StyledButtonWidth.FULL, disabled: !isValid, isLoading: isLoading })] })) }))) })));
}
